import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Check, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { applyMask, MaskOptions } from "shared/utils/masks";

export interface EditableNumberCellProps {
  /** Current value. `null`/`undefined` renders the empty placeholder. */
  value: number | null | undefined;
  /**
   * Persists the new value. Receives `null` when the field is cleared.
   * Must throw when saving fails so the cell can restore the previous value.
   */
  onSave: (value: number | null) => Promise<void>;
  /** Display mask applied to the value in read mode (e.g. "surface"). */
  mask?: MaskOptions;
  /** Accessible name for the input / edit button (Dutch). */
  ariaLabel: string;
  /** Shown when there is no value yet. */
  emptyPlaceholder?: string;
  inputPlaceholder?: string;
  /** Hides the edit affordance (e.g. read-only permissions). */
  isDisabled?: boolean;
  /** Sentry context so failures can be traced back to a record. */
  errorContext?: Record<string, unknown>;
}

/**
 * Inline editable numeric table cell.
 *
 * Read mode renders the (masked) value with a pencil affordance; edit mode
 * renders a numeric input with confirm/cancel. Enter saves, Escape cancels.
 * Every click stops propagation so the component can live inside clickable
 * table rows without triggering row navigation.
 *
 * Deliberately generic: it knows nothing about the entity it edits — the
 * parent supplies the persistence through `onSave`.
 */
export const EditableNumberCell = ({
  value,
  onSave,
  mask = null,
  ariaLabel,
  emptyPlaceholder = "-",
  inputPlaceholder = "0",
  isDisabled = false,
  errorContext,
}: EditableNumberCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the draft in sync when the row is re-rendered with fresh data, but
  // never overwrite what the user is currently typing.
  useEffect(() => {
    if (!isEditing) {
      setDraft(value === null || value === undefined ? "" : String(value));
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const startEditing = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDraft(value === null || value === undefined ? "" : String(value));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(value === null || value === undefined ? "" : String(value));
    setIsEditing(false);
  };

  /**
   * Parses the draft (accepting both "12.5" and "12,5") and persists it.
   * Validation and transport errors are surfaced to the user and to Sentry —
   * a silently failing inline edit is indistinguishable from a saved one.
   */
  const save = async () => {
    const trimmed = draft.trim();
    let parsed: number | null = null;

    if (trimmed !== "") {
      parsed = Number(trimmed.replace(",", "."));
      if (!Number.isFinite(parsed)) {
        toast.error("Voer een geldig getal in");
        return;
      }
      if (parsed < 0) {
        toast.error("Waarde kan niet negatief zijn");
        return;
      }
    }

    // Nothing changed — skip the request entirely.
    const current = value === null || value === undefined ? null : value;
    if (parsed === current) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onSave(parsed);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving inline number cell:", error);
      Sentry.captureException(error, {
        tags: { component: "EditableNumberCell" },
        extra: { ariaLabel, draft, parsed, ...errorContext },
      });
      toast.error("Opslaan mislukt, probeer het opnieuw");
      cancelEditing();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    // 0 is a real measurement, not "no data": only null/undefined (and an empty
    // string coming from a loosely typed API) fall back to the placeholder, so
    // "niet ingevuld" stays distinguishable from "0 m²".
    const hasValue =
      value !== null && value !== undefined && (value as unknown) !== "";

    return (
      <div className="flex items-center gap-1">
        <span
          className={
            hasValue
              ? "text-neutral-primary text-sm"
              : "text-neutral-secondary-mid text-sm"
          }
        >
          {hasValue ? applyMask(value, mask) : emptyPlaceholder}
        </span>
        {!isDisabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-neutral-primary/70 hover:text-moss-primary h-7 w-7"
            aria-label={`${ariaLabel} aanpassen`}
            onClick={startEditing}
          >
            <PencilSimple size={14} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={draft}
        placeholder={inputPlaceholder}
        disabled={isSaving}
        className="h-8 w-20 text-right text-sm"
        onChange={(event) => {
          const next = event.target.value;
          // Only digits with a single optional decimal separator.
          if (next === "" || /^\d*[.,]?\d*$/.test(next)) setDraft(next);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void save();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            cancelEditing();
          }
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-status-good hover:text-status-good/80 h-7 w-7"
        aria-label={`${ariaLabel} opslaan`}
        disabled={isSaving}
        onClick={(event) => {
          event.stopPropagation();
          void save();
        }}
      >
        {isSaving ? (
          <span className="border-neutral-primary/40 h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          <Check size={14} weight="bold" />
        )}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-brick-primary hover:text-brick-primary/80 h-7 w-7"
        aria-label={`${ariaLabel} annuleren`}
        disabled={isSaving}
        onClick={(event) => {
          event.stopPropagation();
          cancelEditing();
        }}
      >
        <X size={14} weight="bold" />
      </Button>
    </div>
  );
};
